import { cache } from "react";
import type { OrderReturnType } from "./types/Order";
import type { ProblemInputType } from "./types/Problem";
import type { ProblemCategoriesReturnType } from "./types/ProblemCategories";

type ReturnErrorType = {
	message: string;
	errors: Record<string, string[]>;
};

const fetchHemlockApi = async <T>(url: string, init: RequestInit) => {
	const apiUrl = process.env.NEXT_PUBLIC_HEMLOCK_API_URL;

	if (apiUrl === undefined) {
		throw new Error("No HEMLOCK API url found");
	}

	const response = await fetch(`${apiUrl}${url}`, init);

	if (!response.ok) {
		if (response.status !== 500) {
			console.error(response);
		} else {
			throw new Error("Failed to fetch HEMLOCK API");
		}

		return (await response.json()) as ReturnErrorType;
	}

	const json = await response.json();

	return typeof json === "object" ? (json as T) : null;
};

export const getProblemCategories = cache(async () =>
	fetchHemlockApi<ProblemCategoriesReturnType>("/v1/public/problem-categories", { method: "GET" }),
);

export const getOrder = cache(async (email: string, increment_id: string) =>
	fetchHemlockApi<OrderReturnType>(`/v1/public/order?email=${email}&increment_id=${increment_id}`, {
		method: "GET",
	}),
);

export const postProblem = cache(async (input: ProblemInputType) => {
	const formData = new FormData();

	formData.append("increment_id", input.increment_id);
	formData.append("email", input.email);

	if (input.comment) formData.append("comment", input.comment);

	input.items.forEach((item, index) => {
		formData.append(`items[${index}][order_item_id]`, item.order_item_id);
		if (item.panel_id) formData.append(`items[${index}][panel_id]`, item.panel_id);
		formData.append(`items[${index}][problem_category_id]`, item.problem_category_id);
		formData.append(`items[${index}][form_type]`, item.form_type);
		formData.append(`items[${index}][solution_category]`, item.solution_category);

		item.images.forEach((image, i) => {
			formData.append(`items[${index}][images][${i}]`, image);
		});

		if (item.comment) formData.append(`items[${index}][comment]`, item.comment);
	});

	type ResponseType = {
		id: string;
		increment_id: string;
		status: { value: Status; label: string };
		created_at: string;
		updated_at: string;
	};

	type Status = "pending" | "in_progress" | "approved" | "rejected" | "waiting_for_customer";

	return fetchHemlockApi<ResponseType>("/v1/public/problems", { method: "POST", body: formData });
});
